/**
 * DevExtreme (esm/__internal/ui/form/m_form.layout_manager.js)
 * Version: 23.2.10
 * Build date: Mon Oct 28 2024
 *
 * Copyright (c) 2012 - 2024 Developer Express Inc. ALL RIGHTS RESERVED
 * Read about DevExtreme licensing here: https://js.devexpress.com/Licensing/
 */
import _extends from "@babel/runtime/helpers/esm/extends";
import "../../../ui/text_box";
import "../../../ui/number_box";
import "../../../ui/check_box";
import "../../../ui/date_box";
import "../../../ui/button";
import registerComponent from "../../../core/component_registrator";
import $ from "../../../core/renderer";
import {
    normalizeIndexes
} from "../../../core/utils/array";
import {
    compileGetter
} from "../../../core/utils/data";
import {
    extend
} from "../../../core/utils/extend";
import {
    each
} from "../../../core/utils/iterator";
import {
    getWidth
} from "../../../core/utils/size";
import {
    isDefined,
    isEmptyObject,
    isFunction,
    isObject,
    type
} from "../../../core/utils/type";
import variableWrapper from "../../../core/utils/variable_wrapper";
import {
    getCurrentScreenFactor,
    hasWindow
} from "../../../core/utils/window";
import eventsEngine from "../../../events/core/events_engine";
import {
    removeEvent
} from "../../../events/remove";
import messageLocalization from "../../../localization/message";
import ResponsiveBox from "../../../ui/responsive_box";
import Widget from "../../../ui/widget/ui.widget";
import {
    renderButtonItem
} from "./components/m_button_item";
import {
    renderEmptyItem
} from "./components/m_empty_item";
import {
    renderFieldItem
} from "./components/m_field_item";
import {
    FIELD_ITEM_CLASS,
    FORM_LAYOUT_MANAGER_CLASS,
    LAYOUT_MANAGER_ONE_COLUMN,
    ROOT_SIMPLE_ITEM_CLASS,
    SIMPLE_ITEM_TYPE,
    SINGLE_COLUMN_ITEM_CONTENT
} from "./constants";
import {
    default as FormItemsRunTimeInfo
} from "./m_form.items_runtime_info";
import {
    convertToRenderFieldItemOptions
} from "./m_form.layout_manager.utils";
var FORM_EDITOR_BY_DEFAULT = "dxTextBox";
var LAYOUT_MANAGER_FIRST_ROW_CLASS = "dx-first-row";
var LAYOUT_MANAGER_LAST_ROW_CLASS = "dx-last-row";
var LAYOUT_MANAGER_FIRST_COL_CLASS = "dx-first-col";
var LAYOUT_MANAGER_LAST_COL_CLASS = "dx-last-col";
var LayoutManager = Widget.inherit({
    _getDefaultOptions() {
        return extend(this.callBase(), {
            layoutData: {},
            readOnly: false,
            colCount: 1,
            colCountByScreen: void 0,
            labelLocation: "left",
            onFieldDataChanged: null,
            onEditorEnterKey: null,
            customizeItem: null,
            alignItemLabels: true,
            minColWidth: 200,
            showRequiredMark: true,
            screenByWidth: null,
            showOptionalMark: false,
            requiredMark: "*",
            labelMode: "outside",
            optionalMark: messageLocalization.format("dxForm-optionalMark"),
            requiredMessage: messageLocalization.getFormatter("dxForm-requiredMessage")
        })
    },
    _setOptionsByReference() {
        this.callBase();
        extend(this._optionsByReference, {
            layoutData: true,
            validationGroup: true
        })
    },
    _init() {
        var layoutData = this.option("layoutData");
        this.callBase();
        this._itemWatchers = [];
        this._itemsRunTimeInfo = new FormItemsRunTimeInfo;
        this._updateReferencedOptions(layoutData);
        this._initDataAndItems(layoutData)
    },
    _dispose() {
        this.callBase();
        this._cleanItemWatchers()
    },
    _initDataAndItems(initialData) {
        this._syncDataWithItems();
        this._updateItems(initialData)
    },
    _syncDataWithItems() {
        var layoutData = this.option("layoutData");
        var userItems = this.option("items");
        if (isDefined(userItems)) {
            userItems.forEach(item => {
                if (item.dataField && void 0 === this._getDataByField(item.dataField)) {
                    var value;
                    if (item.editorOptions) {
                        value = item.editorOptions.value
                    }
                    if (isDefined(value) || item.dataField in layoutData) {
                        this._updateFieldValue(item.dataField, value)
                    }
                }
            })
        }
    },
    _getDataByField(dataField) {
        return dataField ? this.option("layoutData.".concat(dataField)) : null
    },
    _isCheckboxUndefinedStateEnabled(_ref) {
        var {
            allowIndeterminateState: allowIndeterminateState,
            editorType: editorType,
            dataField: dataField
        } = _ref;
        if (true === allowIndeterminateState && "dxCheckBox" === editorType) {
            var nameParts = ["layoutData", ...dataField.split(".")];
            var propertyName = nameParts.pop();
            var layoutData = this.option(nameParts.join("."));
            return layoutData && propertyName in layoutData
        }
        return false
    },
    _updateFieldValue(dataField, value) {
        var layoutData = this.option("layoutData");
        var newValue = value;
        if (!variableWrapper.isWrapped(layoutData[dataField]) && isDefined(dataField)) {
            this.option("layoutData.".concat(dataField), newValue)
        } else if (variableWrapper.isWritableWrapped(layoutData[dataField])) {
            newValue = isFunction(newValue) ? newValue() : newValue;
            layoutData[dataField](newValue)
        }
        this._triggerOnFieldDataChanged({
            dataField: dataField,
            value: newValue
        })
    },
    _triggerOnFieldDataChanged(args) {
        this._createActionByOption("onFieldDataChanged")(args)
    },
    _updateItems(layoutData) {
        var that = this;
        var userItems = this.option("items");
        var isUserItemsExist = isDefined(userItems);
        var customizeItem = that.option("customizeItem");
        var items = isUserItemsExist ? userItems : this._generateItemsByData(layoutData);
        if (isDefined(items)) {
            var processedItems = [];
            each(items, (index, item) => {
                if (that._isAcceptableItem(item)) {
                    item = that._processItem(item);
                    customizeItem && customizeItem(item);
                    if (isObject(item) && false !== variableWrapper.unwrap(item.visible)) {
                        processedItems.push(item)
                    }
                }
            });
            if (!that._itemWatchers.length || !isUserItemsExist) {
                that._updateItemWatchers(items)
            }
            this._setItems(processedItems);
            this._sortItems()
        }
    },
    _cleanItemWatchers() {
        this._itemWatchers.forEach(dispose => {
            dispose()
        });
        this._itemWatchers = []
    },
    _updateItemWatchers(items) {
        var that = this;
        var watch = that._getWatch();
        items.forEach(item => {
            if (isObject(item) && isDefined(item.visible) && isFunction(watch)) {
                that._itemWatchers.push(watch(() => variableWrapper.unwrap(item.visible), () => {
                    that._updateItems(that.option("layoutData"));
                    that.repaint()
                }, {
                    skipImmediate: true
                }))
            }
        })
    },
    _generateItemsByData(layoutData) {
        var result = [];
        if (isDefined(layoutData)) {
            each(layoutData, dataField => {
                result.push({
                    dataField: dataField
                })
            })
        }
        return result
    },
    _isAcceptableItem(item) {
        var itemField = item.dataField || item;
        var itemData = this._getDataByField(itemField);
        return !(isFunction(itemData) && !variableWrapper.isWrapped(itemData))
    },
    _processItem(item) {
        var _a;
        if ("string" === typeof item) {
            item = {
                dataField: item
            }
        }
        if ("object" === typeof item && !item.itemType) {
            item.itemType = SIMPLE_ITEM_TYPE
        }
        if (!isDefined(item.editorType) && isDefined(item.dataField)) {
            var value = this._getDataByField(item.dataField);
            item.editorType = isDefined(value) ? this._getEditorTypeByDataType(type(value)) : FORM_EDITOR_BY_DEFAULT
        }
        if ("dxCheckBox" === item.editorType) {
            item.allowIndeterminateState = null !== (_a = item.allowIndeterminateState) && void 0 !== _a ? _a : true
        }
        return item
    },
    _getEditorTypeByDataType(dataType) {
        switch (dataType) {
            case "number":
                return "dxNumberBox";
            case "date":
                return "dxDateBox";
            case "boolean":
                return "dxCheckBox";
            default:
                return "dxTextBox"
        }
    },
    _sortItems() {
        normalizeIndexes(this._items, "visibleIndex");
        this._sortIndexes()
    },
    _sortIndexes() {
        this._items.sort((itemA, itemB) => {
            var indexA = itemA.visibleIndex;
            var indexB = itemB.visibleIndex;
            var result;
            if (indexA > indexB) {
                result = 1
            } else if (indexA < indexB) {
                result = -1
            } else {
                result = 0
            }
            return result
        })
    },
    _initMarkup() {
        this._itemsRunTimeInfo.clear();
        this.$element().addClass(FORM_LAYOUT_MANAGER_CLASS);
        this.callBase();
        this._renderResponsiveBox()
    },
    _renderResponsiveBox() {
        var templatesInfo = [];
        if (this._items && this._items.length) {
            var colCount = this._getColCount();
            var $container = $("<div>").appendTo(this.$element());
            this._prepareItemsWithMerging(colCount);
            var layoutItems = this._generateLayoutItems();
            this._responsiveBox = this._createComponent($container, ResponsiveBox, this._getResponsiveBoxConfig(layoutItems, colCount, templatesInfo));
            if (!hasWindow()) {
                this._renderTemplates(templatesInfo)
            }
        }
    },
    _itemStateChangedHandler(e) {
        this._refresh()
    },
    _renderTemplates(templatesInfo) {
        var that = this;
        var itemsWithLabelTemplateCount = 0;
        templatesInfo.forEach(_ref2 => {
            var {
                item: item
            } = _ref2;
            var _a;
            if (null === (_a = null === item || void 0 === item ? void 0 : item.label) || void 0 === _a ? void 0 : _a.template) {
                itemsWithLabelTemplateCount++
            }
        });
        each(templatesInfo, (index, info) => {
            switch (info.itemType) {
                case "empty":
                    renderEmptyItem(info);
                    break;
                case "button":
                    that._renderButtonItem(info);
                    break;
                default:
                    that._renderFieldItem(info, itemsWithLabelTemplateCount)
            }
        })
    },
    _getResponsiveBoxConfig(layoutItems, colCount, templatesInfo) {
        var that = this;
        var colCountByScreen = that.option("colCountByScreen");
        var xsColCount = colCountByScreen && colCountByScreen.xs;
        return {
            onItemStateChanged: this._itemStateChangedHandler.bind(this),
            onLayoutChanged() {
                var onLayoutChanged = that.option("onLayoutChanged");
                var isSingleColumnMode = that.isSingleColumnMode();
                if (onLayoutChanged) {
                    that.$element().toggleClass(LAYOUT_MANAGER_ONE_COLUMN, isSingleColumnMode);
                    onLayoutChanged(isSingleColumnMode)
                }
            },
            onContentReady(e) {
                if (hasWindow()) {
                    that._renderTemplates(templatesInfo)
                }
                if (that.option("onLayoutChanged")) {
                    that.$element().toggleClass(LAYOUT_MANAGER_ONE_COLUMN, that.isSingleColumnMode(e.component))
                }
            },
            itemTemplate(e, itemData, itemElement) {
                if (!e.location) {
                    return
                }
                var $itemElement = $(itemElement);
                var itemRenderedCountInPreviousRows = e.location.row * colCount;
                var item = that._items[e.location.col + itemRenderedCountInPreviousRows];
                if (!item) {
                    return
                }
                var itemCssClassList = [item.cssClass];
                $itemElement.toggleClass(SINGLE_COLUMN_ITEM_CONTENT, that.isSingleColumnMode(this));
                if (0 === e.location.row) {
                    itemCssClassList.push(LAYOUT_MANAGER_FIRST_ROW_CLASS)
                }
                if (0 === e.location.col) {
                    itemCssClassList.push(LAYOUT_MANAGER_FIRST_COL_CLASS)
                }
                if (item.itemType === SIMPLE_ITEM_TYPE && that.option("isRoot")) {
                    $itemElement.addClass(ROOT_SIMPLE_ITEM_CLASS)
                }
                var isLastColumn = e.location.col === colCount - 1 || e.location.col + e.location.colspan === colCount;
                var rowsCount = that._getRowsCount();
                var isLastRow = e.location.row === rowsCount - 1;
                if (isLastColumn) {
                    itemCssClassList.push(LAYOUT_MANAGER_LAST_COL_CLASS)
                }
                if (isLastRow) {
                    itemCssClassList.push(LAYOUT_MANAGER_LAST_ROW_CLASS)
                }
                if ("empty" !== item.itemType) {
                    itemCssClassList.push(FIELD_ITEM_CLASS);
                    itemCssClassList.push(that.option("cssItemClass"));
                    if (isDefined(item.col)) {
                        itemCssClassList.push("dx-col-".concat(item.col))
                    }
                }
                templatesInfo.push({
                    itemType: item.itemType,
                    item: item,
                    $parent: $itemElement,
                    rootElementCssClassList: itemCssClassList
                })
            },
            cols: that._generateRatio(colCount),
            rows: that._generateRatio(that._getRowsCount(), true),
            dataSource: layoutItems,
            screenByWidth: that.option("screenByWidth"),
            singleColumnScreen: xsColCount ? false : "xs"
        }
    },
    _getColCount() {
        var colCount = this.option("colCount");
        var colCountByScreen = this.option("colCountByScreen");
        if (colCountByScreen) {
            var screenFactor = this.option("form").getTargetScreenFactor();
            if (!screenFactor) {
                screenFactor = hasWindow() ? getCurrentScreenFactor(this.option("screenByWidth")) : "lg"
            }
            colCount = colCountByScreen[screenFactor] || colCount
        }
        if ("auto" === colCount) {
            if (this._cashedColCount) {
                return this._cashedColCount
            }
            this._cashedColCount = colCount = this._getMaxColCount()
        }
        return colCount < 1 ? 1 : colCount
    },
    _getMaxColCount() {
        if (!hasWindow()) {
            return 1
        }
        var minColWidth = this.option("minColWidth");
        var width = getWidth(this.$element());
        var itemsCount = this._items.length;
        var maxColCount = Math.floor(width / minColWidth) || 1;
        return itemsCount < maxColCount ? itemsCount : maxColCount
    },
    isCachedColCountObsolete() {
        return this._cashedColCount && this._getMaxColCount() !== this._cashedColCount
    },
    _prepareItemsWithMerging(colCount) {
        var items = this._items.slice(0);
        var item;
        var itemsMergedByCol;
        var result = [];
        var j;
        var i;
        for (i = 0; i < items.length; i++) {
            item = items[i];
            result.push(item);
            if (this.option("alignItemLabels") || item.alignItemLabels || item.colSpan) {
                item.col = this._getColByIndex(result.length - 1, colCount)
            }
            if (item.colSpan > 1 && item.col + item.colSpan <= colCount) {
                itemsMergedByCol = [];
                for (j = 0; j < item.colSpan - 1; j++) {
                    itemsMergedByCol.push({
                        merged: true
                    })
                }
                result = result.concat(itemsMergedByCol)
            } else {
                delete item.colSpan
            }
        }
        this._setItems(result)
    },
    _getColByIndex: (index, colCount) => index % colCount,
    _setItems(items) {
        this._items = items;
        this._cashedColCount = null
    },
    _generateLayoutItems() {
        var items = this._items;
        var colCount = this._getColCount();
        var result = [];
        var item;
        var i;
        for (i = 0; i < items.length; i++) {
            item = items[i];
            if (!item.merged) {
                var generatedItem = {
                    location: {
                        row: parseInt(i / colCount),
                        col: this._getColByIndex(i, colCount)
                    }
                };
                if (isDefined(item.disabled)) {
                    generatedItem.disabled = item.disabled
                }
                if (isDefined(item.visible)) {
                    generatedItem.visible = item.visible
                }
                if (isDefined(item.colSpan)) {
                    generatedItem.location.colspan = item.colSpan
                }
                if (isDefined(item.rowSpan)) {
                    generatedItem.location.rowspan = item.rowSpan
                }
                result.push(generatedItem)
            }
        }
        return result
    },
    _renderEmptyItem($container) {
        renderEmptyItem({
            $container: $container
        })
    },
    _renderButtonItem(_ref3) {
        var {
            item: item,
            $parent: $parent,
            rootElementCssClassList: rootElementCssClassList
        } = _ref3;
        var {
            $rootElement: $rootElement,
            buttonInstance: buttonInstance
        } = renderButtonItem({
            item: item,
            $parent: $parent,
            rootElementCssClassList: rootElementCssClassList,
            validationGroup: this.option("validationGroup"),
            createComponentCallback: this._createComponent.bind(this)
        });
        this._itemsRunTimeInfo.add({
            item: item,
            widgetInstance: buttonInstance,
            guid: item.guid,
            $itemContainer: $rootElement
        })
    },
    _renderFieldItem(_ref4, itemsWithLabelTemplateCount) {
        var {
            item: item,
            $parent: $parent,
            rootElementCssClassList: rootElementCssClassList
        } = _ref4;
        var _a, _b;
        var editorValue = this._getDataByField(item.dataField);
        var canAssignUndefinedValueToEditor = false;
        if (void 0 === editorValue) {
            var {
                allowIndeterminateState: allowIndeterminateState,
                editorType: editorType,
                dataField: dataField
            } = item;
            canAssignUndefinedValueToEditor = this._isCheckboxUndefinedStateEnabled({
                allowIndeterminateState: allowIndeterminateState,
                editorType: editorType,
                dataField: dataField
            })
        }
        var name = item.dataField || item.name;
        var formOrLayoutManager = this._getFormOrThis();
        var {
            $fieldEditorContainer: $fieldEditorContainer,
            widgetInstance: widgetInstance,
            $rootElement: $rootElement
        } = renderFieldItem(convertToRenderFieldItemOptions({
            $parent: $parent,
            rootElementCssClassList: rootElementCssClassList,
            item: item,
            name: name,
            editorValue: editorValue,
            canAssignUndefinedValueToEditor: canAssignUndefinedValueToEditor,
            formOrLayoutManager: this._getFormOrThis(),
            createComponentCallback: this._createComponent.bind(this),
            formLabelLocation: this.option("labelLocation"),
            requiredMessageTemplate: this.option("requiredMessage"),
            validationGroup: this.option("validationGroup"),
            editorValidationBoundary: this.option("validationBoundary"),
            editorStylingMode: this.option("form") && this.option("form").option("stylingMode"),
            showColonAfterLabel: this.option("showColonAfterLabel"),
            managerLabelLocation: this.option("labelLocation"),
            template: item.template ? this._getTemplate(item.template) : null,
            labelTemplate: (null === (_a = item.label) || void 0 === _a ? void 0 : _a.template) ? this._getTemplate(item.label.template) : null,
            itemId: this.option("form") && this.option("form").getItemID(name),
            managerMarkOptions: this._getMarkOptions(),
            labelMode: this.option("labelMode"),
            onLabelTemplateRendered: () => {
                this._incTemplateRenderedCallCount();
                if (this._shouldAlignLabelsOnTemplateRendered(formOrLayoutManager, itemsWithLabelTemplateCount)) {
                    formOrLayoutManager._alignLabels(this, this.isSingleColumnMode(formOrLayoutManager))
                }
            }
        }));
        null === (_b = this.option("onFieldItemRendered")) || void 0 === _b ? void 0 : _b();
        if (widgetInstance && item.dataField) {
            this._bindDataField(widgetInstance, item.dataField, item.editorType, $fieldEditorContainer)
        }
        this._itemsRunTimeInfo.add({
            item: item,
            widgetInstance: widgetInstance,
            guid: item.guid,
            $itemContainer: $rootElement
        })
    },
    _incTemplateRenderedCallCount() {
        var _a;
        this._labelTemplateRenderedCallCount = (null !== (_a = this._labelTemplateRenderedCallCount) && void 0 !== _a ? _a : 0) + 1
    },
    _shouldAlignLabelsOnTemplateRendered(formOrLayoutManager, totalItemsWithLabelTemplate) {
        return formOrLayoutManager.option("templatesRenderAsynchronously") && this._labelTemplateRenderedCallCount === totalItemsWithLabelTemplate
    },
    _getMarkOptions() {
        return {
            showRequiredMark: this.option("showRequiredMark"),
            requiredMark: this.option("requiredMark"),
            showOptionalMark: this.option("showOptionalMark"),
            optionalMark: this.option("optionalMark")
        }
    },
    _getFormOrThis() {
        return this.option("form") || this
    },
    _bindDataField(editorInstance, dataField, editorType, $container) {
        var formOrThis = this._getFormOrThis();
        editorInstance.on("enterKey", args => {
            formOrThis._createActionByOption("onEditorEnterKey")(extend(args, {
                dataField: dataField
            }))
        });
        this._createWatcher(editorInstance, $container, dataField);
        this.linkEditorToDataField(editorInstance, dataField, editorType)
    },
    _createWatcher(editorInstance, $container, dataField) {
        var that = this;
        var watch = that._getWatch();
        if (!isFunction(watch)) {
            return
        }
        var dispose = watch(() => that._getDataByField(dataField), () => {
            var fieldValue = that._getDataByField(dataField);
            if ("dxTagBox" === editorInstance.NAME) {
                var editorValue = editorInstance.option("value");
                if (fieldValue !== editorValue && function(array1, array2) {
                        if (!Array.isArray(array1) || !Array.isArray(array2) || array1.length !== array2.length) {
                            return false
                        }
                        for (var i = 0; i < array1.length; i++) {
                            if (array1[i] !== array2[i]) {
                                return false
                            }
                        }
                        return true
                    }(fieldValue, editorValue)) {
                    return
                }
            }
            editorInstance.option("value", fieldValue)
        }, {
            deep: true,
            skipImmediate: true
        }, {
            createWatcherDataField: dataField
        });
        eventsEngine.on($container, removeEvent, dispose)
    },
    _getWatch() {
        if (!isDefined(this._watch)) {
            var formInstance = this.option("form");
            this._watch = formInstance && formInstance.option("integrationOptions.watchMethod")
        }
        return this._watch
    },
    _createComponent($editor, type, editorOptions) {
        var readOnlyState = this.option("readOnly");
        var hasEditorReadOnly = Object.hasOwn(editorOptions, "readOnly");
        var instance = this.callBase($editor, type, _extends(_extends({}, editorOptions), {
            readOnly: !hasEditorReadOnly ? readOnlyState : editorOptions.readOnly
        }));
        var isChangeByForm = false;
        instance.on("optionChanged", args => {
            if ("readOnly" === args.name && !isChangeByForm) {
                hasEditorReadOnly = true
            }
        });
        this.on("optionChanged", args => {
            if ("readOnly" === args.name && !hasEditorReadOnly) {
                isChangeByForm = true;
                instance.option(args.name, args.value);
                isChangeByForm = false
            }
        });
        return instance
    },
    _generateRatio(count, isAutoSize) {
        var result = [];
        var ratio;
        var i;
        for (i = 0; i < count; i++) {
            ratio = {
                ratio: 1
            };
            if (isAutoSize) {
                ratio.baseSize = "auto"
            }
            result.push(ratio)
        }
        return result
    },
    _getRowsCount() {
        return Math.ceil(this._items.length / this._getColCount())
    },
    _updateReferencedOptions(newLayoutData) {
        var layoutData = this.option("layoutData");
        if (isObject(layoutData)) {
            Object.getOwnPropertyNames(layoutData).forEach(property => delete this._optionsByReference["layoutData.".concat(property)])
        }
        if (isObject(newLayoutData)) {
            Object.getOwnPropertyNames(newLayoutData).forEach(property => this._optionsByReference["layoutData.".concat(property)] = true)
        }
    },
    _clearWidget(instance) {
        this._disableEditorValueChangedHandler = true;
        instance.clear();
        this._disableEditorValueChangedHandler = false;
        instance.option("isValid", true)
    },
    _optionChanged(args) {
        if (0 === args.fullName.search("layoutData.")) {
            return
        }
        switch (args.name) {
            case "showRequiredMark":
            case "showOptionalMark":
            case "requiredMark":
            case "optionalMark":
                this._cashedRequiredConfig = null;
                this._invalidate();
                break;
            case "layoutData":
                this._updateReferencedOptions(args.value);
                if (this.option("items")) {
                    if (!isEmptyObject(args.value)) {
                        this._itemsRunTimeInfo.each((_, itemRunTimeInfo) => {
                            if (isDefined(itemRunTimeInfo.item)) {
                                var {
                                    dataField: dataField
                                } = itemRunTimeInfo.item;
                                if (dataField && isDefined(itemRunTimeInfo.widgetInstance)) {
                                    var valueGetter = compileGetter(dataField);
                                    var dataValue = valueGetter(args.value);
                                    var {
                                        allowIndeterminateState: allowIndeterminateState,
                                        editorType: editorType
                                    } = itemRunTimeInfo.item;
                                    if (void 0 !== dataValue || this._isCheckboxUndefinedStateEnabled({
                                            allowIndeterminateState: allowIndeterminateState,
                                            editorType: editorType,
                                            dataField: dataField
                                        })) {
                                        itemRunTimeInfo.widgetInstance.option("value", dataValue)
                                    } else {
                                        this._clearWidget(itemRunTimeInfo.widgetInstance)
                                    }
                                }
                            }
                        })
                    }
                } else {
                    this._initDataAndItems(args.value);
                    this._invalidate()
                }
                break;
            case "items":
                this._cleanItemWatchers();
                this._initDataAndItems(args.value);
                this._invalidate();
                break;
            case "alignItemLabels":
            case "labelLocation":
            case "labelMode":
            case "requiredMessage":
                this._invalidate();
                break;
            case "customizeItem":
                this._updateItems(this.option("layoutData"));
                this._invalidate();
                break;
            case "colCount":
            case "colCountByScreen":
                this._resetColCount();
                break;
            case "minColWidth":
                if ("auto" === this.option("colCount")) {
                    this._resetColCount()
                }
                break;
            case "readOnly":
                break;
            case "width":
                this.callBase(args);
                if ("auto" === this.option("colCount")) {
                    this._resetColCount()
                }
                break;
            case "onFieldDataChanged":
                break;
            default:
                this.callBase(args)
        }
    },
    _resetColCount() {
        this._cashedColCount = null;
        this._invalidate()
    },
    linkEditorToDataField(editorInstance, dataField) {
        this.on("optionChanged", args => {
            if (args.fullName === "layoutData.".concat(dataField)) {
                editorInstance._setOptionWithoutOptionChange("value", args.value)
            }
        });
        editorInstance.on("valueChanged", args => {
            var isValueReferenceType = isObject(args.value) || Array.isArray(args.value);
            if (!this._disableEditorValueChangedHandler && !(isValueReferenceType && args.value === args.previousValue)) {
                this._updateFieldValue(dataField, args.value)
            }
        })
    },
    _dimensionChanged() {
        if ("auto" === this.option("colCount") && this.isCachedColCountObsolete()) {
            this._eventsStrategy.fireEvent("autoColCountChanged")
        }
    },
    updateData(data, value) {
        var that = this;
        if (isObject(data)) {
            each(data, (dataField, fieldValue) => {
                that._updateFieldValue(dataField, fieldValue)
            })
        } else if ("string" === typeof data) {
            that._updateFieldValue(data, value)
        }
    },
    getEditor(field) {
        return this._itemsRunTimeInfo.findWidgetInstanceByDataField(field) || this._itemsRunTimeInfo.findWidgetInstanceByName(field)
    },
    isSingleColumnMode(component) {
        var responsiveBox = this._responsiveBox || component;
        if (responsiveBox) {
            return responsiveBox.option("currentScreenFactor") === responsiveBox.option("singleColumnScreen")
        }
    },
    getItemsRunTimeInfo() {
        return this._itemsRunTimeInfo
    }
});
registerComponent("dxLayoutManager", LayoutManager);
export default LayoutManager;
